/**
 * Countries.
 *
 * A free-text country box is a reporting bug waiting to happen — the same country arrives as
 * three spellings and no filter ever groups them. So the list is fixed, and the picker searches
 * it.
 *
 * **Stored as the name, not the code.** The column already holds names, and a name is legible in
 * a CSV export, a statement and a database console without a lookup table. `countryCode()` is
 * there for the day something needs the two-letter form.
 *
 * ISO 3166-1, short names. Held as one string and split on load rather than 250 object literals,
 * because the object form is four times the bytes and nobody reads either.
 *
 * The default lives here, in the data, and never in the words on a page — see the binding rule in
 * `CLAUDE.md`. Changing where this product is sold means changing this constant, not the copy.
 */

export const DEFAULT_COUNTRY = "Nigeria";

const RAW = [
  "AF Afghanistan",
  "AL Albania",
  "DZ Algeria",
  "AS American Samoa",
  "AD Andorra",
  "AO Angola",
  "AI Anguilla",
  "AG Antigua and Barbuda",
  "AR Argentina",
  "AM Armenia",
  "AW Aruba",
  "AU Australia",
  "AT Austria",
  "AZ Azerbaijan",
  "BS Bahamas",
  "BH Bahrain",
  "BD Bangladesh",
  "BB Barbados",
  "BY Belarus",
  "BE Belgium",
  "BZ Belize",
  "BJ Benin",
  "BM Bermuda",
  "BT Bhutan",
  "BO Bolivia",
  "BA Bosnia and Herzegovina",
  "BW Botswana",
  "BR Brazil",
  "BN Brunei",
  "BG Bulgaria",
  "BF Burkina Faso",
  "BI Burundi",
  "CV Cabo Verde",
  "KH Cambodia",
  "CM Cameroon",
  "CA Canada",
  "KY Cayman Islands",
  "CF Central African Republic",
  "TD Chad",
  "CL Chile",
  "CN China",
  "CO Colombia",
  "KM Comoros",
  "CG Congo",
  "CD Congo (Democratic Republic)",
  "CK Cook Islands",
  "CR Costa Rica",
  "CI Côte d'Ivoire",
  "HR Croatia",
  "CU Cuba",
  "CW Curaçao",
  "CY Cyprus",
  "CZ Czechia",
  "DK Denmark",
  "DJ Djibouti",
  "DM Dominica",
  "DO Dominican Republic",
  "EC Ecuador",
  "EG Egypt",
  "SV El Salvador",
  "GQ Equatorial Guinea",
  "ER Eritrea",
  "EE Estonia",
  "SZ Eswatini",
  "ET Ethiopia",
  "FO Faroe Islands",
  "FJ Fiji",
  "FI Finland",
  "FR France",
  "GF French Guiana",
  "PF French Polynesia",
  "GA Gabon",
  "GM Gambia",
  "GE Georgia",
  "DE Germany",
  "GH Ghana",
  "GI Gibraltar",
  "GR Greece",
  "GL Greenland",
  "GD Grenada",
  "GP Guadeloupe",
  "GU Guam",
  "GT Guatemala",
  "GG Guernsey",
  "GN Guinea",
  "GW Guinea-Bissau",
  "GY Guyana",
  "HT Haiti",
  "HN Honduras",
  "HK Hong Kong",
  "HU Hungary",
  "IS Iceland",
  "IN India",
  "ID Indonesia",
  "IR Iran",
  "IQ Iraq",
  "IE Ireland",
  "IM Isle of Man",
  "IL Israel",
  "IT Italy",
  "JM Jamaica",
  "JP Japan",
  "JE Jersey",
  "JO Jordan",
  "KZ Kazakhstan",
  "KE Kenya",
  "KI Kiribati",
  "KW Kuwait",
  "KG Kyrgyzstan",
  "LA Laos",
  "LV Latvia",
  "LB Lebanon",
  "LS Lesotho",
  "LR Liberia",
  "LY Libya",
  "LI Liechtenstein",
  "LT Lithuania",
  "LU Luxembourg",
  "MO Macao",
  "MG Madagascar",
  "MW Malawi",
  "MY Malaysia",
  "MV Maldives",
  "ML Mali",
  "MT Malta",
  "MH Marshall Islands",
  "MQ Martinique",
  "MR Mauritania",
  "MU Mauritius",
  "YT Mayotte",
  "MX Mexico",
  "FM Micronesia",
  "MD Moldova",
  "MC Monaco",
  "MN Mongolia",
  "ME Montenegro",
  "MS Montserrat",
  "MA Morocco",
  "MZ Mozambique",
  "MM Myanmar",
  "NA Namibia",
  "NR Nauru",
  "NP Nepal",
  "NL Netherlands",
  "NC New Caledonia",
  "NZ New Zealand",
  "NI Nicaragua",
  "NE Niger",
  "NG Nigeria",
  "NU Niue",
  "MK North Macedonia",
  "KP North Korea",
  "NO Norway",
  "OM Oman",
  "PK Pakistan",
  "PW Palau",
  "PS Palestine",
  "PA Panama",
  "PG Papua New Guinea",
  "PY Paraguay",
  "PE Peru",
  "PH Philippines",
  "PL Poland",
  "PT Portugal",
  "PR Puerto Rico",
  "QA Qatar",
  "RE Réunion",
  "RO Romania",
  "RU Russia",
  "RW Rwanda",
  "BL Saint Barthélemy",
  "KN Saint Kitts and Nevis",
  "LC Saint Lucia",
  "MF Saint Martin",
  "VC Saint Vincent and the Grenadines",
  "WS Samoa",
  "SM San Marino",
  "ST Sao Tome and Principe",
  "SA Saudi Arabia",
  "SN Senegal",
  "RS Serbia",
  "SC Seychelles",
  "SL Sierra Leone",
  "SG Singapore",
  "SX Sint Maarten",
  "SK Slovakia",
  "SI Slovenia",
  "SB Solomon Islands",
  "SO Somalia",
  "ZA South Africa",
  "KR South Korea",
  "SS South Sudan",
  "ES Spain",
  "LK Sri Lanka",
  "SD Sudan",
  "SR Suriname",
  "SE Sweden",
  "CH Switzerland",
  "SY Syria",
  "TW Taiwan",
  "TJ Tajikistan",
  "TZ Tanzania",
  "TH Thailand",
  "TL Timor-Leste",
  "TG Togo",
  "TO Tonga",
  "TT Trinidad and Tobago",
  "TN Tunisia",
  "TR Türkiye",
  "TM Turkmenistan",
  "TC Turks and Caicos Islands",
  "TV Tuvalu",
  "UG Uganda",
  "UA Ukraine",
  "AE United Arab Emirates",
  "GB United Kingdom",
  "US United States",
  "UY Uruguay",
  "UZ Uzbekistan",
  "VU Vanuatu",
  "VA Vatican City",
  "VE Venezuela",
  "VN Vietnam",
  "VG Virgin Islands (British)",
  "VI Virgin Islands (U.S.)",
  "YE Yemen",
  "ZM Zambia",
  "ZW Zimbabwe",
];

export interface Country {
  /** ISO 3166-1 alpha-2. */
  code: string;
  name: string;
}

export const COUNTRIES: Country[] = RAW.map((line) => ({
  code: line.slice(0, 2),
  name: line.slice(3),
}));

const BY_NAME = new Map(COUNTRIES.map((country) => [country.name.toLowerCase(), country]));
const BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]));

/** The two-letter code for a stored country name, if it is one we know. */
export function countryCode(name: string | null | undefined): string | null {
  if (!name) return null;
  return BY_NAME.get(name.trim().toLowerCase())?.code ?? null;
}

/** The stored name for a code — the reverse, for imports that arrive as codes. */
export function countryName(code: string | null | undefined): string | null {
  if (!code) return null;
  return BY_CODE.get(code.trim().toUpperCase())?.name ?? null;
}

/**
 * Search by name or code. Matches from the start of any word, so "guin" finds Guinea, Guinea-Bissau
 * and Papua New Guinea, but "ea" does not drag in half the list.
 */
export function searchCountries(query: string, limit = 40): Country[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return COUNTRIES.slice(0, limit);
  if (needle.length === 2) {
    const exact = BY_CODE.get(needle.toUpperCase());
    if (exact) return [exact, ...COUNTRIES.filter((c) => c !== exact && matches(c, needle))].slice(0, limit);
  }
  return COUNTRIES.filter((country) => matches(country, needle)).slice(0, limit);
}

function matches(country: Country, needle: string): boolean {
  const name = country.name.toLowerCase();
  if (name.startsWith(needle)) return true;
  return name.split(/[\s(-]+/).some((word) => word.startsWith(needle));
}

/**
 * Dialling codes.
 *
 * Held apart from the name list so neither has to be reformatted when the other changes, and
 * keyed by ISO code because that is the only part of a country that does not get renamed.
 */
const DIAL_RAW =
  "AF 93,AL 355,DZ 213,AS 1684,AD 376,AO 244,AI 1264,AG 1268,AR 54,AM 374,AW 297,AU 61,AT 43,AZ 994," +
  "BS 1242,BH 973,BD 880,BB 1246,BY 375,BE 32,BZ 501,BJ 229,BM 1441,BT 975,BO 591,BA 387,BW 267,BR 55," +
  "BN 673,BG 359,BF 226,BI 257,CV 238,KH 855,CM 237,CA 1,KY 1345,CF 236,TD 235,CL 56,CN 86,CO 57,KM 269," +
  "CG 242,CD 243,CK 682,CR 506,CI 225,HR 385,CU 53,CW 599,CY 357,CZ 420,DK 45,DJ 253,DM 1767,DO 1809," +
  "EC 593,EG 20,SV 503,GQ 240,ER 291,EE 372,SZ 268,ET 251,FO 298,FJ 679,FI 358,FR 33,GF 594,PF 689," +
  "GA 241,GM 220,GE 995,DE 49,GH 233,GI 350,GR 30,GL 299,GD 1473,GP 590,GU 1671,GT 502,GG 44,GN 224," +
  "GW 245,GY 592,HT 509,HN 504,HK 852,HU 36,IS 354,IN 91,ID 62,IR 98,IQ 964,IE 353,IM 44,IL 972,IT 39," +
  "JM 1876,JP 81,JE 44,JO 962,KZ 7,KE 254,KI 686,KW 965,KG 996,LA 856,LV 371,LB 961,LS 266,LR 231," +
  "LY 218,LI 423,LT 370,LU 352,MO 853,MG 261,MW 265,MY 60,MV 960,ML 223,MT 356,MH 692,MQ 596,MR 222," +
  "MU 230,YT 262,MX 52,FM 691,MD 373,MC 377,MN 976,ME 382,MS 1664,MA 212,MZ 258,MM 95,NA 264,NR 674," +
  "NP 977,NL 31,NC 687,NZ 64,NI 505,NE 227,NG 234,NU 683,MK 389,KP 850,NO 47,OM 968,PK 92,PW 680," +
  "PS 970,PA 507,PG 675,PY 595,PE 51,PH 63,PL 48,PT 351,PR 1787,QA 974,RE 262,RO 40,RU 7,RW 250," +
  "BL 590,KN 1869,LC 1758,MF 590,VC 1784,WS 685,SM 378,ST 239,SA 966,SN 221,RS 381,SC 248,SL 232," +
  "SG 65,SX 1721,SK 421,SI 386,SB 677,SO 252,ZA 27,KR 82,SS 211,ES 34,LK 94,SD 249,SR 597,SE 46," +
  "CH 41,SY 963,TW 886,TJ 992,TZ 255,TH 66,TL 670,TG 228,TO 676,TT 1868,TN 216,TR 90,TM 993,TC 1649," +
  "TV 688,UG 256,UA 380,AE 971,GB 44,US 1,UY 598,UZ 998,VU 678,VA 39,VE 58,VN 84,VG 1284,VI 1340," +
  "YE 967,ZM 260,ZW 263";

const DIAL_BY_CODE = new Map<string, string>(
  DIAL_RAW.split(",").map((entry) => {
    const [code, dial] = entry.split(" ");
    return [code, dial] as [string, string];
  }),
);

/**
 * Which country to show when a dialling code belongs to several.
 *
 * `+44` is four places and `+1` is twenty-four. Splitting a stored number can only ever guess, so
 * it guesses the largest and lets the person correct it — the number itself is unaffected either
 * way, since what is stored is the code and the digits, not the country.
 */
const SHARED_DIAL_PREFERENCE: Record<string, string> = {
  "1": "US",
  "7": "RU",
  "39": "IT",
  "44": "GB",
  "262": "RE",
  "590": "GP",
};

const CODES_BY_DIAL = new Map<string, string>();
for (const [code, dial] of DIAL_BY_CODE) {
  const preferred = SHARED_DIAL_PREFERENCE[dial];
  if (preferred) CODES_BY_DIAL.set(dial, preferred);
  else if (!CODES_BY_DIAL.has(dial)) CODES_BY_DIAL.set(dial, code);
}

/** The dialling code for a country, given either its ISO code or its stored name. */
export function dialCode(country: string | null | undefined): string | null {
  if (!country) return null;
  const trimmed = country.trim();
  if (trimmed.length === 2) return DIAL_BY_CODE.get(trimmed.toUpperCase()) ?? null;
  const code = countryCode(trimmed);
  return code ? (DIAL_BY_CODE.get(code) ?? null) : null;
}

/** Every country that has a dialling code, in name order, for a picker to list. */
export interface DiallingCountry extends Country {
  dial: string;
}

export const DIALLING_COUNTRIES: DiallingCountry[] = COUNTRIES.filter((country) => DIAL_BY_CODE.has(country.code)).map(
  (country) => ({ ...country, dial: DIAL_BY_CODE.get(country.code) as string }),
);

const DIALLING_BY_CODE = new Map(DIALLING_COUNTRIES.map((country) => [country.code, country]));

/** One country of the dialling list, by ISO code. */
export function diallingCountry(code: string | null | undefined): DiallingCountry | null {
  if (!code) return null;
  return DIALLING_BY_CODE.get(code.trim().toUpperCase()) ?? null;
}

/** Search the dialling list by country name, ISO code, or the code itself typed as `234` or `+234`. */
export function searchDiallingCountries(query: string, limit = 40): DiallingCountry[] {
  const needle = query.trim().replace(/^\+/, "").toLowerCase();
  if (!needle) return DIALLING_COUNTRIES.slice(0, limit);
  if (/^\d+$/.test(needle)) {
    return DIALLING_COUNTRIES.filter((country) => country.dial.startsWith(needle)).slice(0, limit);
  }
  // A two-letter query is almost always the ISO code — "NG" must find Nigeria, whose name starts
  // with neither of those letters as a word.
  const exact = needle.length === 2 ? DIALLING_BY_CODE.get(needle.toUpperCase()) : undefined;
  const byName = DIALLING_COUNTRIES.filter((country) => country !== exact && matches(country, needle));
  return (exact ? [exact, ...byName] : byName).slice(0, limit);
}

/**
 * The two halves of a stored phone number.
 *
 * One column, one string, so an export and a database console still show a number somebody can
 * ring. The split is for the form only, and is re-joined on save.
 */
export interface PhoneParts {
  /** ISO country code, e.g. `NG`. Null when the stored value carries no dialling code. */
  country: string | null;
  /** Dialling code without the `+`, e.g. `234`. */
  dial: string | null;
  /** What the person actually types: the number after the dialling code. */
  number: string;
}

/**
 * Split a stored number into a country and the rest.
 *
 * Longest dialling code first, so `+1684` is American Samoa rather than the United States with a
 * number beginning 684. Anything not starting with `+` is left whole — a local number typed
 * before this control existed is still a number, and rewriting it on a guess would be worse than
 * showing it as it is.
 */
export function splitPhone(value: string | null | undefined): PhoneParts {
  const raw = (value ?? "").trim();
  if (!raw.startsWith("+")) return { country: null, dial: null, number: raw };

  const digits = raw.slice(1).replace(/[^\d]/g, "");
  for (let length = 4; length >= 1; length -= 1) {
    const candidate = digits.slice(0, length);
    const country = CODES_BY_DIAL.get(candidate);
    if (country) return { country, dial: candidate, number: digits.slice(length) };
  }
  return { country: null, dial: null, number: raw };
}

/**
 * Put the two halves back together.
 *
 * Returns an empty string when there is no number, so a blank field stores nothing rather than a
 * dialling code on its own — `+234` is not a phone number, and a reminder sent to it fails
 * silently.
 */
export function joinPhone(country: string | null | undefined, number: string | null | undefined): string {
  const digits = (number ?? "").replace(/[^\d]/g, "").replace(/^0+/, "");
  if (!digits) return "";
  const dial = dialCode(country);
  return dial ? `+${dial} ${digits}` : digits;
}
